import { inject, Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AmplifyApiService } from 'src/app/core/services/amplify-api.service';
import { AmplifyAuthService } from 'src/app/core/services/amplify-auth.service';
import { CampaignState } from 'src/app/features/campaigns/redux';
import { ContentState } from 'src/app/features/content/redux';
import {
  BuildDrbFail,
  BuildDrbSuccess,
  LoadDiffFail,
  LoadDiffSuccess,
  LoadDrb,
  loadDrbAssociatedItemsFail,
  loadDrbAssociatedItemsSuccess,
  LoadDrbByVersionFail,
  LoadDrbByVersionSuccess,
  LoadDrbSuccess,
  LoadDrbVersionsSuccess,
  NoDrbDiff,
  NoDrbDraft,
  SaveComplete,
  SaveFail
} from 'src/app/features/drbs/redux/drb.actions';
import { Campaign } from 'src/app/models/campaign';
import { Content } from 'src/app/models/content';
import { Drb } from 'src/app/models/drb-models/drb';
import { DrbValues } from 'src/app/models/drb-models/drb-values';
import { APIS } from 'src/app/shared/constants/api-constants';
import { BUNDLE_NAMES, ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { ENVIRONMENT } from 'src/environments/environment';
import { DrbState, DrbStateModel } from '../redux';

@Injectable({ providedIn: 'any' })
export class DrbService {
  public content$: Observable<any[]> = inject(Store).select(ContentState.content);
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);

  public drb_api = APIS.DRB;
  public campaigns: Campaign[] = [];
  public contents: Content[] = [];

  constructor(
    private store: Store,
    private toastrService: NbToastrService,
    private apiService: AmplifyApiService,
    private amplifyAuthService: AmplifyAuthService
  ) {
    this.campaign$.subscribe((data) => (this.campaigns = data));
    this.content$.subscribe((data) => (this.contents = data));
  }

  public setupAffectedItems(item: any, env: string) {
    if ('payload' in item) {
      const payload = item.payload;
      if (env === ENVIRONMENTS.STAGE) {
        let stageCampaigns = [];
        let stageContent = [];
        if ('stage_campaign_ids' in payload || 'stage_content_ids' in payload) {
          stageCampaigns = this.campaigns.filter((campaign) =>
            payload.stage_campaign_ids.includes(campaign.id.toString())
          );
          stageContent = this.contents.filter((content) => payload.stage_content_ids.includes(content.content_id));

          return [stageCampaigns, stageContent];
        }
      } else if (env === ENVIRONMENTS.PROD) {
        if ('prod_campaign_ids' in payload || 'prod_content_ids' in payload) {
          return [payload.prod_campaign_ids, payload.prod_content_ids];
        }
      }
    } else {
      return [[], []];
    }
  }

  public mapResponse(response: Drb) {
    const localResponse = JSON.parse(JSON.stringify(response));
    return localResponse;
  }

  public async loadDrb(bundle_name: string, reload = false) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.loadDrbAmplify(token, bundle_name, reload);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public loadDrbAmplify(token: string, bundle_name: string, reload = false) {
    this.apiService
      .get(this.drb_api, `/${ENVIRONMENT.name}/drb`, {
        queryStringParameters: { token, version: 'draft', bundle_name }
      })
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbSuccess(this.mapResponse(response), bundle_name));
      })
      .catch((error) => {
        if (error.response.data.error === 'Draft version not found') {
          this.store.dispatch(NoDrbDraft);
        } else {
          this.toastrService.show('', $localize`Fetching DRB Failed`, {
            status: 'danger'
          });
        }
      });
  }

  public async loadDrbByVersion(version: string, bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.loadDrbByVersionAmplify(token, version, bundle_name);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public loadDrbByVersionAmplify(token: string, version: string, bundle_name: string, reload = false) {
    this.apiService
      .get(this.drb_api, `/${ENVIRONMENT.name}/drb`, { queryStringParameters: { token, version, bundle_name } })
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB ${version} Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbByVersionSuccess(this.mapResponse(response), bundle_name));
      })
      .catch(() => {
        this.toastrService.show('', $localize`Fetching DRB ${version} Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new LoadDrbByVersionFail());
      });
  }

  public loadDrbAssociatedItems(drb_id: string) {
    this.apiService
      .get(this.drb_api, `/${ENVIRONMENT.name}/associated-resources?drb_id=${drb_id}`, {})
      .then((response) => {
        this.toastrService.show('', $localize`Fetching DRB Associated Items Complete`, {
          status: 'success'
        });
        this.store.dispatch(new loadDrbAssociatedItemsSuccess(response));
      })
      .catch((error) => {
        if (error.response.data.error === 'DRB ID not found') {
          this.store.dispatch(new loadDrbAssociatedItemsSuccess({}));
        } else {
          console.error(error);
          this.toastrService.show('', $localize`Fetching DRB Associated Items Failed`, {
            status: 'danger'
          });
          this.store.dispatch(new loadDrbAssociatedItemsFail());
        }
      });
  }

  public putDrbAmplify(newDrbDraft: Drb, bundle_name: string) {
    this.apiService
      .put(this.drb_api, `/${ENVIRONMENT.name}/drb`, { body: newDrbDraft })
      .then(() => {
        this.store.dispatch(new SaveComplete(this.mapResponse(newDrbDraft), bundle_name));
      })
      .catch((error) => {
        this.store.dispatch(new SaveFail(error));
      });
  }

  public postDrbAmplify(newDrbDraft: Drb, bundle_name: string) {
    this.apiService
      .post(this.drb_api, `/${ENVIRONMENT.name}/drb`, { body: newDrbDraft })
      .then(() => {
        this.store.dispatch(new LoadDrb(bundle_name));
      })
      .catch((error) => {
        this.store.dispatch(new SaveFail(error));
      });
  }

  public async loadDrbVersions(bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.loadDrbVersionsAmplify(token, bundle_name);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public loadDrbVersionsAmplify(token: string, bundle_name: string, reload = false) {
    this.apiService
      .get(this.drb_api, `/${ENVIRONMENT.name}/drb`, { queryStringParameters: { token, all: true, bundle_name } })
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Versions Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbVersionsSuccess(response, bundle_name));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Fetching DRB Versions Failed`, {
          status: 'danger'
        });
      });
  }

  public async loadDiff(bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.loadDiffAmplify(token, bundle_name);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public loadDiffAmplify(token: string, bundle_name: string, reload = false) {
    const endpoint = ENVIRONMENT.isProduction ? 'validate-prod' : 'validate';
    this.apiService
      .get(this.drb_api, `/${ENVIRONMENT.name}/${endpoint}`, { queryStringParameters: { token, bundle_name } })
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Diff Complete`, {
            status: 'success'
          });
        }
        const data = response;
        this.store.dispatch(new LoadDiffSuccess(data));
      })
      .catch((error) => {
        if (error.response.data === 'No DRBs available for validation') {
          this.store.dispatch(NoDrbDiff);
        } else {
          this.toastrService.show('', $localize`Fetching DRB Diff Failed`, {
            status: 'danger'
          });
          this.store.dispatch(new LoadDiffFail());
        }
      });
  }

  public async buildDrb(bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.buildDrbAmplify(bundle_name, token);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public buildDrbAmplify(bundle_name: string, token: string, reload = false) {
    const endpoint = ENVIRONMENT.isProduction ? 'deploy-prod' : 'build';
    this.apiService
      .post(this.drb_api, `/${ENVIRONMENT.name}/${endpoint}`, {
        body: { bundle_name },
        queryStringParameters: { token }
      })
      .then(() => {
        if (reload) {
          this.toastrService.show('', $localize`Building DRB Started`, {
            status: 'success'
          });
        }
        this.store.dispatch(BuildDrbSuccess);
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Building DRB Failed`, {
          status: 'danger'
        });
        this.store.dispatch(BuildDrbFail);
      });
  }

  public getDrbIds() {
    const ids: Record<string, string[]> = {
      string: [],
      file: [],
      image: [],
      link: [],
      video: []
    };
    for (const key in BUNDLE_NAMES) {
      const drb: Drb = this.store.selectSnapshot(DrbState.getByKey(key.toLowerCase() as keyof DrbStateModel));
      if (drb && drb.version) {
        ids.string = ids.string.concat(drb.xliff.en.map((item) => item.id));
        ids.file = ids.file.concat(drb.source.files.map((item) => item.id));
        ids.image = ids.image.concat(drb.source.images.map((item) => item.id));
        ids.link = ids.link.concat(drb.source.links.map((item) => item.id));
        ids.video = ids.video.concat(drb.source.videos.map((item) => item.id));
      }
    }
    return ids;
  }

  public getDrbValues(drbIds: string[]): DrbValues {
    const values: DrbValues = {
      string: {},
      file: {},
      image: {},
      link: {},
      video: {}
    };
    for (const drbId of drbIds) {
      let drb: Drb = this.store.selectSnapshot(DrbState.getByKey(BUNDLE_NAMES.MARKETING));
      if (drbId.startsWith('mkt_t_')) {
        drb = this.store.selectSnapshot(DrbState.getByKey(BUNDLE_NAMES.MARKETING_TRANSLATIONS));
      }
      const string = drb.xliff.en.find((item) => item.id == drbId);
      if (string) {
        values.string[drbId] = string;
        continue;
      }
      const file = drb.source.files.find((item) => item.id == drbId);
      if (file) {
        values.file[drbId] = file.location;
        continue;
      }
      const image = drb.source.images.find((item) => item.id == drbId);
      if (image) {
        values.image[drbId] = image.location + '/' + image.file;
        continue;
      }
      const link = drb.source.links.find((item) => item.id == drbId);
      if (link) {
        values.link[drbId] = link.location;
        continue;
      }
      const video = drb.source.videos.find((item) => item.id == drbId);
      if (video) {
        values.video[drbId] = video.location;
        continue;
      }
    }
    return values;
  }
}
